import { useQuery } from "@apollo/client/react";
import { STAFF_SHOPIFY_ORDER } from "@staff-app/graphql-client";
import type {
  ShopifyOrderDetail,
  ShopifyOrderRow,
} from "@staff-app/shared-types";
import { Button } from "@staff-app/ui";
import { ExternalLink, X } from "lucide-react";
import { CopyButton, trackingClipboard } from "@/components/CopyButton";
import { formatMoney } from "@/lib/money";
import { StatusBadge } from "./OrderStatusBadge";

/**
 * Shopify's order payload, as returned. Typed loosely on purpose: the API hands this over as JSON
 * precisely so the drawer can render whatever Shopify sends without a type in three repos needing
 * an edit every time their schema gains a field. Nothing here is filtered or sorted on.
 */
interface OrderJson {
  name?: string;
  createdAt?: string;
  email?: string;
  phone?: string;
  note?: string;
  discountCodes?: string[];
  shippingAddress?: Record<string, string | null>;
  totalPriceSet?: { shopMoney?: { amount?: string; currencyCode?: string } };
  lineItems?: {
    nodes?: Array<{ title?: string; quantity?: number; sku?: string }>;
  };
  fulfillments?: Array<{
    status?: string;
    createdAt?: string;
    trackingInfo?: Array<{ number?: string; company?: string; url?: string }>;
  }>;
}

/**
 * Full date AND time, spelled out.
 *
 * The table shows the date alone to keep the row narrow; this is where the exact moment lives, so
 * it is stated in full rather than abbreviated. Falls back to the row's own createdAt while the
 * detail query is still in flight, so the field never flashes empty.
 */
function creationDate(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function addressLines(a?: Record<string, string | null>): string[] {
  if (!a) return [];
  return [
    a.name,
    a.address1,
    a.address2,
    [a.city, a.province, a.zip].filter(Boolean).join(" "),
    a.country,
    a.phone,
  ].filter((l): l is string => Boolean(l && l.trim()));
}

export function OrderDetailDrawer({
  row,
  onClose,
}: {
  row: ShopifyOrderRow;
  onClose: () => void;
}) {
  const { data, loading, error } = useQuery<{
    staffShopifyOrder: ShopifyOrderDetail | null;
  }>(STAFF_SHOPIFY_ORDER, { variables: { id: row.id } });

  let order: OrderJson = {};
  try {
    order = data?.staffShopifyOrder?.json
      ? (JSON.parse(data.staffShopifyOrder.json) as OrderJson)
      : {};
  } catch {
    order = {};
  }

  const items = order.lineItems?.nodes ?? [];
  const tracking = (order.fulfillments ?? []).flatMap(
    (f) => f.trackingInfo ?? [],
  );
  const address = addressLines(order.shippingAddress);

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/30"
      onClick={onClose}
    >
      <aside
        className="h-full w-full max-w-xl overflow-y-auto bg-background p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="order-detail-drawer"
      >
        <header className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold">{row.name}</h2>
            <p className="text-sm text-muted-foreground">
              {row.customerEmail ?? row.shipToName ?? "—"}
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </Button>
        </header>

        <div className="mb-4 flex gap-2">
          <StatusBadge
            status={row.displayFulfillmentStatus}
            kind="fulfillment"
          />
          <StatusBadge status={row.displayFinancialStatus} kind="financial" />
        </div>

        <section className="mb-6">
          <h3 className="mb-1 text-sm font-medium">Creation Date</h3>
          <p
            className="text-sm text-muted-foreground"
            data-testid="order-created-at"
          >
            {creationDate(order.createdAt ?? row.createdAt)}
          </p>
        </section>

        {loading && (
          <p className="text-sm text-muted-foreground">Loading order…</p>
        )}
        {error && (
          <p
            className="text-sm text-destructive"
            data-testid="order-detail-error"
          >
            {error.message}
          </p>
        )}

        {!loading && !error && (
          <div className="space-y-6">
            <section>
              <h3 className="mb-2 text-sm font-medium">Tracking</h3>
              {tracking.length ? (
                <ul className="space-y-1 text-sm">
                  {tracking.map((t, i) => (
                    <li key={i} className="flex items-center gap-2">
                      {/* The NUMBER is the link, matching the table — it is the thing an operator
                          reaches for, so it should be the thing that is clickable. "Track" stays
                          as an explicit second affordance for anyone who does not think of a
                          monospace string as a link. */}
                      {t.number && t.url ? (
                        <a
                          href={t.url}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="font-mono text-primary hover:underline"
                        >
                          {t.number}
                        </a>
                      ) : (
                        <span className="font-mono">{t.number ?? "—"}</span>
                      )}
                      {t.company && (
                        <span className="text-muted-foreground">
                          ({t.company})
                        </span>
                      )}
                      {t.number && (
                        <CopyButton
                          {...trackingClipboard(t.number, t.url)}
                          label="Tracking number"
                          testId={`copy-tracking-detail-${i}`}
                        />
                      )}
                      {t.url && (
                        <a
                          href={t.url}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="inline-flex items-center gap-1 text-primary hover:underline"
                        >
                          Track <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No tracking yet — the order has not shipped.
                </p>
              )}
            </section>

            <section>
              <h3 className="mb-2 text-sm font-medium">Items</h3>
              {items.length ? (
                <ul className="space-y-1 text-sm">
                  {items.map((li, i) => (
                    <li key={i} className="flex justify-between gap-4">
                      <span>
                        {li.quantity ?? 1} × {li.title ?? "Item"}
                        {li.sku && (
                          <span className="ml-2 text-xs text-muted-foreground">
                            {li.sku}
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">—</p>
              )}
            </section>

            {/* Its own section, label over value like Creation Date and Ship to — the drawer reads
                as one list of labelled facts rather than an invoice with a ruled total. Falls back
                to the row's own amount so it is right immediately, not after the detail lands. */}
            <section>
              <h3 className="mb-1 text-sm font-medium">Total</h3>
              <p
                className="text-sm text-muted-foreground"
                data-testid="order-total"
              >
                {formatMoney(
                  order.totalPriceSet?.shopMoney?.amount ?? row.totalAmount,
                  order.totalPriceSet?.shopMoney?.currencyCode ??
                    row.totalCurrency,
                )}
              </p>
            </section>

            <section>
              <h3 className="mb-2 text-sm font-medium">Ship to</h3>
              {address.length ? (
                <address className="text-sm not-italic text-muted-foreground">
                  {address.map((l, i) => (
                    <div key={i}>{l}</div>
                  ))}
                </address>
              ) : (
                <p className="text-sm text-muted-foreground">—</p>
              )}
              {row.deliveryMethod && (
                <p className="mt-2 text-sm text-muted-foreground">
                  via {row.deliveryMethod}
                </p>
              )}
            </section>

            {/* The discount code is how a device order traces back to the coupon we issued. Shown
                as text for now — resolving it to the OptiSigns account is the planned phase 2. */}
            {Boolean(order.discountCodes?.length) && (
              <section>
                <h3 className="mb-2 text-sm font-medium">Discount codes</h3>
                <p className="font-mono text-sm">
                  {order.discountCodes?.join(", ")}
                </p>
              </section>
            )}

            {order.note && (
              <section>
                <h3 className="mb-2 text-sm font-medium">Note</h3>
                <p className="whitespace-pre-wrap text-sm text-muted-foreground">
                  {order.note}
                </p>
              </section>
            )}
          </div>
        )}
      </aside>
    </div>
  );
}
